/**
 * Group courses for individualised students (#821) — the client mirror of
 * `GroupCoursesModule` / `RouteDto.cs`. Dates are ISO `YYYY-MM-DD` strings.
 */

export interface GroupCourseSession {
  id: string;
  date: string;
  periodId: string;
}

export interface GroupCourseRegistration {
  id: string;
  frequentationId: string;
  registeredAt: string;
  droppedAt?: string | null;
}

export interface GroupCourse {
  id: string;
  learningProgramId: string;
  learningModuleId: string;
  teacherId: string;
  name?: string | null;
  calendarId: string;
  schedulePatternId: string;
  /** Bit `i` set = weekday `i` (see group-course-form.ts for the exact mapping). */
  weekdayMask: number;
  periodIds: string[];
  startDate: string;
  endDate: string;
  /** Populated by the detail route only. */
  sessions?: GroupCourseSession[];
  /** Active registrations only, populated by the detail route only. */
  registrations?: GroupCourseRegistration[];
  sessionCount?: number;
  activeRegistrationCount?: number;
}

/** The create/edit body, and the preview body. */
export interface GroupCourseInput {
  learningProgramId: string;
  learningModuleId: string;
  teacherId: string;
  name?: string | null;
  calendarId: string;
  schedulePatternId: string;
  weekdayMask: number;
  periodIds: string[];
  startDate: string;
  endDate: string;
}

export interface GeneratedSession {
  date: string;
  periodId: string;
}

/** A registrant a propagating write could not be applied to, and why (French, from the server). */
export interface SkippedRegistrant {
  frequentationId: string;
  date: string;
  periodId: string;
  reason: string;
}

/** An unconfirmed assignment of another competency a course row would replace (`ReplacementDto`). */
export interface GroupCourseReplacement {
  presenceId: string;
  date: string;
  periodId: string;
  learningModuleId: string;
  moduleNumber: string;
}

export interface GroupCourseRefusal {
  date: string;
  periodId: string;
  reason: string;
}

export interface GroupCourseListQuery {
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortDesc?: boolean;
  programId?: string;
  teacherId?: string;
}

export interface GroupCoursePage {
  items: GroupCourse[];
  total: number;
  page: number;
  pageSize: number;
}

/** Answer of the update route: the course plus the registrants the regeneration skipped. */
export interface GroupCourseUpdateResult {
  course: GroupCourse;
  skipped: SkippedRegistrant[];
}

/** A future session a recurrence edit would recompute although it was edited by hand (#821). */
export interface LostSession {
  date: string;
  periodId: string;
  /** True for a session cancelled by hand that would come back; false for one moved or added by hand that would go. */
  cancelled: boolean;
}

/** What a recurrence edit would throw away — the body of the update route's 409. */
export interface RegenerationLosses {
  sessions: LostSession[];
  /** Unconfirmed registrant rows the edit would archive or replace. */
  affectedRowCount: number;
}

/** A recurrence edit either saves, or asks first because it would recompute hand-made changes. */
export type UpdateGroupCourseOutcome =
  | ({ kind: 'saved' } & GroupCourseUpdateResult)
  | { kind: 'needsConfirmation'; losses: RegenerationLosses };

/**
 * Maps a failed update onto the confirmation request: a 409 carrying `losses`. Anything else is
 * a real error, and `null` tells the caller to let it through.
 */
export function toRegenerationLosses(status: number, body: unknown): RegenerationLosses | null {
  if (status !== 409) return null;
  const losses = (body && typeof body === 'object' ? (body as Record<string, unknown>).losses : undefined);
  if (!losses || typeof losses !== 'object') return null;
  const record = losses as Record<string, unknown>;
  return {
    sessions: Array.isArray(record.sessions) ? record.sessions as LostSession[] : [],
    affectedRowCount: typeof record.affectedRowCount === 'number' ? record.affectedRowCount : 0,
  };
}

/** The list's metrics band over the WHOLE filtered list, not the page on screen. */
export interface GroupCourseSummary {
  upcoming: number;
  running: number;
  activeRegistrations: number;
}

/**
 * What the create dialog pre-fills once a program is chosen (`GET /api/GroupCourses/Defaults`);
 * each value is null when the server cannot tell.
 */
export interface GroupCourseDefaults {
  schedulePatternId: string | null;
  calendarId: string | null;
}

/** Answer of the add/move session routes. */
export interface GroupCourseSessionResult {
  session: GroupCourseSession;
  skipped: SkippedRegistrant[];
}

/**
 * An added or moved session either saves, or asks first because writing the registrants' rows on
 * its slot would replace unconfirmed assignments of other competencies (the 409 of the session
 * routes, re-sent with `confirmReplacements`).
 */
export type SessionWriteOutcome =
  | ({ kind: 'saved' } & GroupCourseSessionResult)
  | { kind: 'needsConfirmation'; replacements: GroupCourseReplacement[] };

/**
 * Maps a failed session add/move onto the confirmation request: a 409 carrying `replacements`.
 * Anything else is a real error, and `null` tells the caller to let it through.
 */
export function toSessionReplacements(status: number, body: unknown): GroupCourseReplacement[] | null {
  if (status !== 409) return null;
  const replacements = body && typeof body === 'object'
    ? (body as Record<string, unknown>).replacements
    : undefined;
  return Array.isArray(replacements) ? replacements as GroupCourseReplacement[] : null;
}

/** The first still-takeable course of a group-given module on a probed cell. */
export interface GroupCourseAvailability {
  courseId: string;
  /** The course's teacher: credited on every row a take writes. */
  teacherId: string;
  sessionCount: number;
  firstDate: string;
  lastDate: string;
}

export type TakeCourseResult =
  | { kind: 'taken' }
  | { kind: 'needsConfirmation'; replacements: GroupCourseReplacement[] }
  | { kind: 'refused'; refusals: GroupCourseRefusal[] };

/**
 * Maps the answer of a take route onto the union: 409 carries the rows the take would replace
 * (`replacements`), 400 the sessions it cannot be written for (`refusals`). Any other 400 (a plain
 * `{ error }` refusal) or status is not an outcome of the take — `null` tells the caller to let
 * the error through.
 */
export function toTakeCourseResult(status: number, body: unknown): TakeCourseResult | null {
  if (status >= 200 && status < 300) return { kind: 'taken' };
  const record = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  if (status === 409 && Array.isArray(record.replacements)) {
    return { kind: 'needsConfirmation', replacements: record.replacements as GroupCourseReplacement[] };
  }
  if (status === 400 && Array.isArray(record.refusals)) {
    return { kind: 'refused', refusals: record.refusals as GroupCourseRefusal[] };
  }
  return null;
}
